import { isNgomOrVnemTenantName } from '../tenant/onboarding/constants'
import type { ConductorCadenceQueueRow, ConductorWorkItem } from './conductor.types'

/** Seed QA accounts (Test LLC / Test LLC 2). Match the monorepo set. */
const CADENCE_EXCLUDED_ACCOUNT_NUMBERS = new Set(['1211130-0', '1211130-1'])

function isCadenceExcludedAccount(row: { accountNumber: string | null, accountName: string | null }): boolean {
  if (row.accountNumber != null && CADENCE_EXCLUDED_ACCOUNT_NUMBERS.has(row.accountNumber)) {
    return true
  }
  return isNgomOrVnemTenantName(row.accountName)
}

export const ACTIVE_WORK_STATUSES = ['OPEN', 'IN_PROGRESS'] as const

export function formatConductorTimestamp(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

export function formatConductorDate(iso: string): string {
  const d = new Date(iso)
  // dueAt is now + SLA duration (timestamptz), not a picked calendar day at midnight UTC.
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString()
}

/** Matches backend rollup sentinel for null workflow stages. */
export function formatConductorStage(stage: string | null | undefined): string {
  return stage ?? '(none)'
}

export function isWorkItemOverdue(item: ConductorWorkItem, now = Date.now()): boolean {
  if (!ACTIVE_WORK_STATUSES.includes(item.status as (typeof ACTIVE_WORK_STATUSES)[number])) {
    return false
  }
  if (item.dueAt == null || item.resolvedAt != null) {
    return false
  }
  return new Date(item.dueAt).getTime() < now
}

export function lastValidPageIndex(total: number, resultsPerPage: number): number {
  if (total <= 0 || resultsPerPage <= 0) {
    return 0
  }
  return Math.max(0, Math.ceil(total / resultsPerPage) - 1)
}

export function truncateJson(value: unknown, maxChars = 4000): string {
  const raw = JSON.stringify(value, null, 2)
  if (raw.length <= maxChars) {
    return raw
  }
  return `${raw.slice(0, maxChars)}\n… (truncated)`
}

export function mutationErrorMessage(error: unknown): string {
  if (error != null && typeof error === 'object') {
    const record = error as { code?: unknown; message?: unknown; data?: { message?: unknown } }
    // RTK .unwrap() rejects with axiosBaseQuery's APIErrors: { code, message, meta }.
    if (typeof record.code === 'string' && typeof record.message === 'string') {
      return record.message
    }
    if (typeof record.data?.message === 'string') {
      return record.data.message
    }
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'Request failed'
}

export interface PaginatedSlice {
  total: number
  results: unknown[]
  resultsPerPage: number
}

/** Distinguish an empty collection from a stale page index beyond the last page. */
export function paginatedListMode(slice: PaginatedSlice): 'empty' | 'page-pending' | 'content' {
  if (slice.total === 0) {
    return 'empty'
  }
  if (slice.results.length === 0) {
    return 'page-pending'
  }
  return 'content'
}

export function shouldShowPaginator(total: number, resultsPerPage: number): boolean {
  return total > resultsPerPage
}

export function isDueOverdue(dueAt: string | null, now = Date.now()): boolean {
  return dueAt != null && new Date(dueAt).getTime() < now
}

/** Chip label and owner for billing-cadence-triage steps. Keep in sync with
 * apps/king-conductor/playbooks/billing-cadence-triage.playbook.md. */
const CADENCE_STEPS: Readonly<Record<string, { chip: string; ownerRole: string }>> = {
  'restore-bill-feed': { chip: 'feed', ownerRole: 'billing-ops' },
  'issue-missing-invoice': { chip: 'invoice', ownerRole: 'billing-ops' },
  'reconcile-billing-status': { chip: 'coherent', ownerRole: 'billing-ops' },
  'decide-close-or-salvage': { chip: 'salvage', ownerRole: 'collections' },
}

export function cadenceStepChip(stepKey: string): string {
  return CADENCE_STEPS[stepKey]?.chip ?? stepKey.replaceAll('-', ' ')
}

export function tenantAccountPath(instance: { tenantCuid: string | null, accountNumber: string | null }): string | null {
  if (instance.tenantCuid == null || instance.accountNumber == null) {
    return null
  }
  return `/tenant/tenants/${instance.tenantCuid}?serviceAccountNumber=${encodeURIComponent(instance.accountNumber)}`
}

const KING_ENERGY_MAIL = /@kingenergy\.com$/i

export function displayAssignee(userId: string | null): string {
  if (userId == null || userId === '') {
    return '—'
  }
  return userId.replace(KING_ENERGY_MAIL, '')
}

export function cadenceRowHasOwner(
  row: Pick<ConductorCadenceQueueRow, 'ownerRole' | 'openSteps'>,
  ownerRole: string
): boolean {
  if (row.ownerRole === ownerRole) {
    return true
  }
  return row.openSteps.some((step) => CADENCE_STEPS[step]?.ownerRole === ownerRole)
}

export type CadenceClientFilters = {
  gateId?: string
  subShape?: string
  ownerRole?: string
  overdueOnly?: boolean
  search?: string
  now?: number
}

export function filterCadenceRows(
  rows: readonly ConductorCadenceQueueRow[],
  filters: CadenceClientFilters
): ConductorCadenceQueueRow[] {
  const now = filters.now ?? Date.now()
  const needle = filters.search?.trim().toLowerCase() ?? ''
  return rows.filter((row) => {
    if (isCadenceExcludedAccount(row)) {
      return false
    }
    if (filters.gateId != null && filters.gateId !== '' && row.gateId !== filters.gateId) {
      return false
    }
    if (filters.subShape != null && filters.subShape !== '' && row.subShape !== filters.subShape) {
      return false
    }
    if (filters.ownerRole != null && filters.ownerRole !== '' && !cadenceRowHasOwner(row, filters.ownerRole)) {
      return false
    }
    if (filters.overdueOnly === true && !isDueOverdue(row.dueAt, now)) {
      return false
    }
    if (needle !== '') {
      const number = (row.accountNumber ?? '').toLowerCase()
      const name = (row.accountName ?? '').toLowerCase()
      if (!number.includes(needle) && !name.includes(needle)) {
        return false
      }
    }
    return true
  })
}

export function sortWorkItemsForDetail(items: ConductorWorkItem[]): ConductorWorkItem[] {
  const isActive = (item: ConductorWorkItem): boolean =>
    ACTIVE_WORK_STATUSES.includes(item.status as (typeof ACTIVE_WORK_STATUSES)[number])
  return [...items.filter(isActive), ...items.filter((item) => !isActive(item))]
}

export function isConductorNotFound(error: unknown): boolean {
  if (error == null || typeof error !== 'object') {
    return false
  }
  const e = error as { statusCode?: number, code?: string }
  return e.statusCode === 404 || e.code === 'entity-not-found'
}
